import { CSSProperties } from "react";
import styled from "styled-components";

import {
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from "@util/constants";
import { colors, colorsRGB, DEFAULT_FONT, fontSizes } from "./constants";
import { Color, Dimensions, FontWeights } from "./types";

export const Overlay = styled.div<{ zIndex?: number }>`
  height: 100%;
  width: 100%;
  position: fixed;
  z-index: ${props => props.zIndex ?? 3};
  left: 0;
  top: 0;
  background-color: ${colorsRGB.black(0.5)};
  overflow: hidden;
  transition: 0.5s;
`;

export const P = styled.p<{
  color?: Color | string;
  fontSize?: number;
  tabletFontSize?: number;
  laptopFontSize?: number;
  noMargin?: boolean;
  marginTop?: number;
  marginBottom?: number;
  width?: number;
  lineHeight?: number;
  underlined?: boolean;
  opacity?: number;
  isMobileStyling?: boolean;
  display?: CSSProperties["display"];
  whiteSpace?: CSSProperties["whiteSpace"];
  cursor?: CSSProperties["cursor"];
  padding?: string;
  textAlign?: string;
  letterSpacing?: string;
  margin?: string;
  userSelect?: string;
  bold?: boolean;
  fontWeight?: string | false;
  wordBreak?: string;
}>`
  text-decoration-line: ${props => (props.underlined ? "underline" : "none")};
  background-color: transparent;
  ${({ bold }) => bold == true && `font-weight: 600;`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ letterSpacing }) => letterSpacing && `letter-spacing: ${letterSpacing};`}
  ${({ cursor }) => cursor && `cursor: ${cursor};`}
  ${({ userSelect }) => userSelect && `user-select: ${userSelect};`}
  ${({ color }) => {
    if (!color) return "";
    if (colors[color as Color]) return `color: ${colors[color as Color]};`;
    return `color: ${color};`;
  }}
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px;`}
  ${({ lineHeight }) =>
    lineHeight ? `line-height: ${lineHeight}px;` : `line-height: 1.2;`}
  ${({ margin }) => margin && `margin: ${margin} !important;`}
  ${({ noMargin }) => noMargin && `margin: 0px;`}
  ${({ width }) => width && `width: ${width}px;`}
  ${({ padding }) => padding && `padding: ${padding};`}
  ${({ display }) => display && `display: ${display};`}
  ${({ opacity }) => opacity && `opacity: ${opacity};`}
  ${({ whiteSpace }) => whiteSpace && `white-space: ${whiteSpace};`}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  ${({ wordBreak }) => wordBreak && `word-break: ${wordBreak};`}

  white-space: break-spaces;

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${fontSizes.p.mobile}px;
    line-height: ${fontSizes.p.mobile * 1.2}px;
  }
  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    ${({ tabletFontSize }) =>
      tabletFontSize && `font-size: ${tabletFontSize}px;`}
  }
  @media only screen and (min-width: 1280px) and (max-width: 1480px) {
    ${({ laptopFontSize }) =>
      laptopFontSize && `font-size: ${laptopFontSize}px;`}
  }
  ${({ isMobileStyling }) =>
    isMobileStyling &&
    `@media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 80%;
    text-align: center;
    margin: auto;
  }`}
`;

export const A = styled.a<{
  color?: Color;
  underlined?: boolean;
  bold?: boolean;
  hoverColor?: Color;
  opacity?: number;
  margin?: string;
  fontWeight?: string;
}>`
  color: ${props =>
    props.color ? colors[props.color] : "var(--theme-primary-color)"};
  ${({ underlined }) =>
    underlined ? "text-decoration: underline;" : "text-decoration: none;"}
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ bold }) => bold && "font-weight: bold;"}
  ${({ opacity }) => opacity && `opacity: ${opacity};`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ hoverColor }) =>
    hoverColor &&
    `&:hover {
    color: ${colors[hoverColor]};
  }`}
`;

export const H1 = styled.h1<{
  noMargin?: boolean;
  fontWeight?: string;
  fontSize?: number;
  color?: Color;
  fontFamily?: string;
  lineHeight?: string;
}>`
  color: ${props =>
    props.color ? colors[props.color] : "var(--theme-primary-color)"};
  font-size: ${props => props.fontSize ?? fontSizes.h1.default}px;
  ${({ noMargin }) => noMargin && `margin: 0px;`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}
  ${({ lineHeight }) => lineHeight && `line-height: ${lineHeight};`}


  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${props => !props.fontSize && `font-size: ${fontSizes.h1.mobile}px;`}
    line-height: 1.2;
  }
`;
export const H2 = styled.h2<{
  color?: Color;
  margin?: string;
  fontWeight?: FontWeights;
  fontSize?: number;
  mobileFontSize?: number;
  fontFamily?: string;
}>`
  font-size: ${fontSizes.h2.default}px;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px;`};
  white-space: initial;
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ color }) => color && `color: ${colors[color]};`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${fontSizes.h2.mobile}px;

    ${({ mobileFontSize }) =>
      mobileFontSize && `font-size: ${mobileFontSize}px;`};
    line-height: 1.2;
  }
`;

export const H3 = styled.h3<{
  color?: Color;
  margin?: string;
  fontWeight?: string;
  fontSize?: number;
  width?: string;
  tabletWidth?: string;
  fontFamily?: string;
}>`
  font-size: ${fontSizes.h3.default}px;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px`};
  ${({ width }) => width && `width: ${width};`};
  white-space: initial;
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ color }) => color && `color: ${colors[color]};`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    ${({ tabletWidth }) => tabletWidth && `width: ${tabletWidth};`};
  }
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${fontSizes.h3.mobile}px;
    line-height: 1.2;
  }
`;

export const H4 = styled.h4<{
  color?: Color;
  margin?: string;
  fontWeight?: FontWeights;
  fontFamily?: string;
  fontSize?: number;
}>`
  font-size: ${fontSizes.h4.default}px;
  white-space: initial;
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ color }) => color && `color: ${colors[color]};`}
  ${({ fontWeight }) => fontWeight && `font-weight: ${fontWeight};`}
  ${({ fontFamily }) => fontFamily && `font-family: ${fontFamily};`}
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px`};

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${fontSizes.h4.mobile}px;
    line-height: 1.2;
  }
`;

export const VerticalSeparator = styled.div<{
  color?: Color;
  width?: string;
  height?: string;
  opacity?: number;
  shouldShowMobile?: boolean;
  margin?: string;
}>`
  border-left: 1.5px solid
    ${props =>
      props.color ? colors[props.color] : "var(--theme-primary-color)"};
  height: ${props => (props.height ? props.height : `auto`)};
  width: ${props => (props.width ? props.width : `20px`)};
  ${({ opacity }) => opacity && `opacity: ${opacity};`}
  ${({ margin }) => margin && `margin: ${margin};`}
`;

export const Separator = styled.div<{
  color?: Color;
  height?: string;
  opacity?: number;
  thickness?: number;
  shouldHideDesktop?: boolean;
  marginBottom?: number;
  width?: string;
  margin?: string;
}>`
  ${({ marginBottom }) =>
    marginBottom ? `margin-bottom: ${marginBottom}px;` : `margin-bottom: 10px`};
  border-bottom: ${props => props.thickness ?? 1}px solid
    ${props =>
      props.color ? colors[props.color] : "var(--theme-primary-color)"};
  ${({ width }) => width && `width: ${width ?? "100%"};`}
  height: ${props => props.height ?? `20px`};
  ${({ opacity }) => opacity && `opacity: ${opacity};`}

  ${({ width }) => width && `width: ${width};`}
  ${({ margin }) => margin && `margin: ${margin};`}


  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    ${({ shouldHideDesktop }) => shouldHideDesktop && `display:block`}
  }
  @media only screen and (min-width: ${TABLET_BREAKPOINT}px) {
    ${({ shouldHideDesktop }) => shouldHideDesktop && `display:none`}
  }
`;

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: Color | Queries.Maybe<string> | undefined;
  backgroundRGBColor?: string;
  aspect?: number;
  flexDirection?: CSSProperties["flexDirection"];
  flex?: number | string;
  flexGrow?: number;
  flexWrap?: CSSProperties["flexWrap"];
  position?: CSSProperties["position"];
  justifyContent?: CSSProperties["justifyContent"];
  alignItems?: CSSProperties["alignItems"];
  alignSelf?: CSSProperties["alignSelf"];
  mobileAlignSelf?: CSSProperties["alignSelf"];
  borderRadius?: number;
  overflow?: CSSProperties["overflow"];
  paddingVertical?: number;
  paddingHorizontal?: number;
  img?: string;
  isMobileColumn?: boolean;
  mobilePadding?: string;
  mobileMargin?: string;
  tabletMargin?: string;
  laptopWidth?: string;
  tabletWidth?: string;
  tabletHeight?: string;
  mobileWidth?: string;
  mobileJustifyContent?: CSSProperties["justifyContent"];
  mobileDimensions?: Dimensions;
  tabletDimensions?: Dimensions;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  height?: string;
  minHeight?: string;
  opacity?: number;
  margin?: string;
  padding?: string;
  cursor?: CSSProperties["cursor"];
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  showOnTablet?: boolean;
  zIndex?: number;
  display?: CSSProperties["display"];
  visibility?: CSSProperties["visibility"];
  rightBorder?: number;
  color?: Color;
  gap?: string;
  rowGap?: string;
}

export const Container = styled.div<ContainerProps>`
  box-sizing: border-box;
  display: ${props => props.display ?? "flex"};
  flex-direction: ${props => props.flexDirection ?? "initial"};
  justify-content: ${props => props.justifyContent ?? "initial"};
  align-items: ${props => props.alignItems ?? "initial"};
  align-self: ${props => props.alignSelf ?? "initial"};
  overflow: ${props => props.overflow ?? "initial"};
  border-radius: ${props => props.borderRadius ?? 0}px;
  background-size: cover;
  ${({ aspect }) => aspect && `aspect-ratio: ${aspect};`}
  ${({ color }) => color && `color: ${colors[color]};`}
  ${({ zIndex }) => zIndex && `z-index: ${zIndex};`}
  ${({ flex }) => flex && `flex: ${flex};`}
  ${({ visibility }) => visibility && `visibility: ${visibility};`}
  ${({ position }) => position && `position: ${position};`}
  ${({ flexGrow }) => flexGrow && `flex-grow: ${flexGrow};`}
  ${({ flexWrap }) => flexWrap && `flex-wrap: ${flexWrap};`}
  ${({ cursor }) => cursor && `cursor: ${cursor};`}
  ${({ gap }) => gap && `gap: ${gap};`}
  ${({ rowGap }) => rowGap && `row-gap: ${rowGap};`}
  ${({ paddingVertical }) =>
    paddingVertical &&
    `padding: 0px ${paddingVertical}px 0px ${paddingVertical}px;`}
    ${({ paddingHorizontal }) =>
    paddingHorizontal &&
    `padding: ${paddingHorizontal}px 0px ${paddingHorizontal}px 0px;`}
  ${({ img }) => img && `background-image: url(${img});`}
  ${({ backgroundColor }) =>
    backgroundColor &&
    `background-color: ${
      colors[backgroundColor as Color]
        ? colors[backgroundColor as Color]
        : backgroundColor
    };`}
  ${({ backgroundRGBColor }) =>
    backgroundRGBColor && `background-color: ${backgroundRGBColor};`}
  ${({ width }) => width && `width: ${width};`}
  ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  ${({ height }) => height && `height: ${height};`}
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight};`}
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ padding }) => padding && `padding: ${padding};`}
  ${({ opacity }) => opacity && `opacity: ${opacity};`}
  ${({ showOnTablet }) => showOnTablet && `display: none;`}
  ${({ rightBorder }) =>
    rightBorder &&
    `border-top-right-radius: ${rightBorder}px; border-bottom-right-radius:${rightBorder}px;`}

  @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px) {
    ${({ laptopWidth }) => laptopWidth && `width: ${laptopWidth};`};
  }

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    ${({ isMobileColumn }) =>
      isMobileColumn &&
      `
      flex-direction: column;
      height: auto;
      align-items: center;
      width: 100%;
    `}

    ${({ mobilePadding }) => mobilePadding && `padding: ${mobilePadding};`}

    ${({ tabletDimensions }) =>
      tabletDimensions &&
      tabletDimensions.height &&
      `height: ${tabletDimensions.height};`}
    ${({ tabletDimensions }) =>
      tabletDimensions &&
      tabletDimensions.width &&
      `width: ${tabletDimensions.width};`}

      ${({ hideOnTablet }) => hideOnTablet && `display: none;`}
      ${({ showOnTablet }) => showOnTablet && `display: flex !important;`}
      ${({ tabletWidth }) => tabletWidth && `width: ${tabletWidth};`};
    ${({ tabletHeight }) => tabletHeight && `height: ${tabletHeight};`};
    ${({ tabletMargin }) => tabletMargin && `margin: ${tabletMargin};`};
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${({ mobileDimensions }) =>
      mobileDimensions &&
      mobileDimensions.height &&
      `height: ${mobileDimensions.height};`}
    ${({ mobileDimensions }) =>
      mobileDimensions &&
      mobileDimensions.width &&
      `width: ${mobileDimensions.width};`}

    ${({ hideOnMobile }) => hideOnMobile && `display: none;`}
    ${({ mobileAlignSelf }) =>
      mobileAlignSelf && `align-self: ${mobileAlignSelf}`}
        ${({ mobileJustifyContent }) =>
      mobileJustifyContent && `justify-content: ${mobileJustifyContent};`}
      ${({ mobileWidth }) => mobileWidth && `width: ${mobileWidth};`};
    ${({ mobileMargin }) => mobileMargin && `margin: ${mobileMargin};`};
  }
`;

export const GridContainer = styled.div<{
  gridTemplateRow?: string;
  width?: string;
  height?: string;
  margin?: string;
  repeat?: number | string;
  mobileRepeat?: number;
  tabletRepeat?: number;
  columnGap?: number;
  rowGap?: number;
  mobileColumnGap?: number;
  mobileRowGap?: number;
  rtl?: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(${props => props.repeat ?? 4}, 1fr);
  ${({ gridTemplateRow }) =>
    gridTemplateRow && `grid-template-row: ${gridTemplateRow};`}
  ${({ columnGap }) => columnGap && `grid-column-gap: ${columnGap}px;`}
  ${({ rowGap }) => rowGap && `grid-row-gap: ${rowGap}px;`}
  ${({ width }) => width && `width: ${width};`}
  ${({ height }) => height && `height: ${height};`}
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ rtl }) => rtl && `direction: rtl;`}

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    grid-template-columns: repeat(${props => props.tabletRepeat ?? 2}, 1fr);
  }
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: repeat(${props => props.mobileRepeat ?? 1}, 1fr);
    ${({ mobileColumnGap }) =>
      mobileColumnGap && `grid-column-gap: ${mobileColumnGap}px;`}
    ${({ mobileRowGap }) => mobileRowGap && `grid-row-gap: ${mobileRowGap}px;`}
  }
`;

export const TextButton = styled(P)<{
  color?: Color;
  hoverColor?: Color;
  underlined?: boolean;
  lineHeight?: number;
  margin?: string;
  fontSizeLaptopDown?: number;
  bold?: boolean;
  fontSize?: number;
  display?: CSSProperties["display"];
}>`
  line-height: ${props => props.lineHeight ?? 18}px;
  font-size: ${props =>
    props.fontSize ? `${props.fontSize}px` : `${fontSizes.p.default}px`};
  text-decoration-line: ${props => (props.underlined ? "underline" : "none")};
  color: ${props =>
    props.color ? colors[props.color] : "var(--theme-primary-color)"};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: ${props => (props.bold ? "bold" : "normal")};
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ display }) => display && `display: ${display};`}

  &:focus {
    outline: 0;
  }
  &:hover {
    text-shadow: 0 0 0.5px ${colors.white};
    text-decoration: underline;
    text-underline-offset: 7px;
  }
  @media screen and (min-width: 1280px) and (max-width: 1480px) {
    font-size: ${props =>
      props.fontSizeLaptopDown
        ? `${props.fontSizeLaptopDown}px`
        : props.fontSize
        ? `${props.fontSize}px`
        : `${fontSizes.p.default}px`};
  }
`;

export const GiveMeSomeSpace = styled.div<{ space?: number }>`
  padding: ${props => props.space ?? 10}px;
`;

export const Button = styled.button<{
  color?: Color;
  hoverColour?: Color;
  padding?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
  border?: string;
  margin?: string;
  mobileMargin?: string;
  backgroundColour?: Color;
}>`
  ${({ margin }) => margin && `margin: ${margin};`}
  width: ${props => (props.width ? props.width : "fit-content")};
  height: ${props => (props.height ? props.height : "fit-content")};
  padding: ${props => (props.padding ? props.padding : "10px 50px")};
  background-color: ${props =>
    props.backgroundColour ? colors[props.backgroundColour] : "transparent"};
  border-radius: ${props => (props.borderRadius ? props.borderRadius : "20px")};
  border: ${props => (props.border ? props.border : "1.5px solid #3d3935")};

  font-family: ${DEFAULT_FONT};
  font-weight: bold;
  letter-spacing: 0.08em;

  font-size: 14px;
  cursor: pointer;
  color: ${props =>
    props.color ? colors[props.color] : "var(--theme-primary-color)"};

  &:hover {
    color: ${props =>
      props.hoverColour ? colors[props.hoverColour] : "white"};
    background-color: ${props =>
      props.color ? colors[props.color] : colors.barkersDarkGray};
    border: ${props =>
      props.hoverColour
        ? `1.5px solid ${colors[props.hoverColour]}`
        : "1.5px solid #3d3935"};
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${({ mobileMargin }) => mobileMargin && `margin: ${mobileMargin};`}
  }
`;

export const StyledOverlay = styled(Container)<{
  height?: string;
  rightGradient?: boolean;
  opacity?: number;
}>`
  background-color: rgba(0, 0, 0, ${props => props.opacity ?? 0.1});
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  /* height: ${props => (props.height ? props.height : "550px")}; */
  transform: translateZ(0);

  ${({ rightGradient }) =>
    rightGradient &&
    `background: linear-gradient(90deg, rgba(0, 0, 0, .6), rgba(0, 0, 0, 0));`}
`;

export const HeroH3 = styled.h3<{ fontWeight?: string }>`
  font-size: 24px;
  font-weight: ${props => (props.fontWeight ? props.fontWeight : "300")};
  font-family: var(--theme-font-sub-header);
  margin-bottom: 12px;

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 18px;
  }
`;

export const FlexGrid = styled(Container)<{
  gridMargin?: string;
  laptopGridMargin?: string;
  tabletGridMargin?: string;
  mobileGridMargin?: string;
  isCollectionPreview?: boolean;
  wrap?: boolean;
}>`
  box-sizing: border-box;
  flex-flow: ${props => (props.wrap ? "wrap" : "initial")};
  ${props => props.gridMargin && `column-gap: ${props.gridMargin};`}

  width: 100%;
  > div {
    box-sizing: border-box;
  }
  ${props =>
    props.laptopGridMargin &&
    `
    @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px) {
     column-gap: ${props.laptopGridMargin};
    }
  `}

  ${props =>
    props.tabletGridMargin &&
    `
    @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
      column-gap: ${props.tabletGridMargin};
    }
  `}

  ${props =>
    props.isCollectionPreview &&
    `
    @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
      flex-direction: column-reverse;
      align-items: center;
    }
  `}

  ${props =>
    props.mobileGridMargin &&
    `
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      column-gap: ${props.mobileGridMargin};
    }
  `}
`;

export const CartBubble = styled(Container)`
  background: var(--theme-primary-color);
  color: ${colors.white};
  width: 18px;
  height: 18px;
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  top: -10px;
  right: -10px;
  display: flex;

  span {
    font-size: 11px;
  }
`;

export const PaperContainer = styled(Container)`
  background-position: bottom;
  background-size: 50%;
  background-repeat: repeat;
  background-color: white;
`;

export const ImageContainer = styled(Container)<{
  isGift?: boolean;
  cardColour?: string;
}>`
  position: relative;
  height: 0;
  padding-bottom: ${({ isGift }) => (isGift ? "87%" : "131.8%")};
  width: 100%;
  margin-bottom: 35px;
  background-color: ${({ cardColour }) =>
    cardColour ? cardColour : "var(--theme-card-color)"};

  &:before {
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    content: "";
    display: block;
    background-size: 700px;
  }

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    margin-bottom: 25px;
  }
`;

export const ImageWrapper = styled(Container)<{
  isGift?: boolean;
}>`
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: ${({ isGift }) => (isGift ? "0" : "10%")};
`;

export const GridColumnSpan = styled.div<{
  gridColumnWidth?: number;
  justifySelf?: string;
  gridColumnWidthMob?: number;
  paddingBottom?: number;
  width?: number;
}>`
  grid-column: span
    ${props => (props.gridColumnWidth ? props.gridColumnWidth : 1)};
  justify-self: ${props => (props.justifySelf ? props.justifySelf : "start")};
  width: ${props => props.width}%;
  padding-bottom: ${props => (props.paddingBottom ? props.paddingBottom : 5)}px;
  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: ${props =>
      props.gridColumnWidthMob ? props.gridColumnWidthMob : 1};
  }
`;

export const FavoriteContainer = styled(Container)`
  transition: opacity 0.3s ease;
  background-color: var(--theme-button-color);
  width: 38px;
  height: 36px;
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  pointer-events: auto !important;
  position: relative;

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    width: 27px;
    height: 27px;
  }
`;
